import { Layout } from '@/components/layout';
import { Badge } from '@/components/ui/badge';
import { Shield, Award, Users, Target, Heart, TrendingUp, CheckCircle } from 'lucide-react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';

const AboutPage = () => {
  return (
    <>
      <Helmet>
        <title>About NutraFuel Supplements® - Built by Athletes, Trusted by Thousands</title>
        <meta name="description" content="Learn about NutraFuel Supplements - India's most trusted sports nutrition brand. Founded by athletes to eliminate fake supplements and provide only authentic, certified products." />
        <meta name="keywords" content="about nutrafuel, authentic supplements india, nutrafuel story, trusted supplement store, athlete founded brand" />
        <link rel="canonical" href="https://nutrafuel.in/about" />
        <meta property="og:title" content="About NutraFuel Supplements - Built by Athletes" />
        <meta property="og:description" content="Founded by athletes to eliminate fake supplements from India. 100% authentic products, trusted by thousands." />
        <meta property="og:url" content="https://nutrafuel.in/about" />
      </Helmet>

      <Layout>
        {/* Hero Section */}
        <section className="relative pt-32 md:pt-40 pb-20 bg-gradient-to-b from-primary/10 via-background to-background overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px]"></div>
          </div>
          
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <Badge className="mb-6 bg-gold text-foreground text-sm px-6 py-2">
                Our Story
              </Badge>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-bold text-foreground leading-tight mb-6">
                Built by Athletes.
                <span className="block text-primary mt-2">Trusted by Thousands.</span>
              </h1>
              
              <p className="text-lg sm:text-xl text-muted-foreground leading-relaxed max-w-3xl mx-auto">
                NutraFuel Supplements was founded by athletes who experienced the frustration of counterfeit supplements in India. We&apos;re on a mission to protect the fitness community with 100% authentic, certified products.
              </p>
            </div>
          </div>
        </section>

        {/* Brand Story */}
        <section className="py-20 bg-background">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <Heart className="w-6 h-6 text-primary" />
                </div>
                <h2 className="text-3xl font-display font-bold text-foreground">
                  Our Brand Story
                </h2>
              </div>

              <div className="space-y-6 text-muted-foreground leading-relaxed">
                <p className="text-lg">
                  NutraFuel Supplements was born from a painful reality that every serious athlete in India knows too well: <strong className="text-foreground">the plague of fake supplements flooding the market.</strong>
                </p>

                <p>
                  Our founders, a group of competitive athletes and fitness professionals, spent years navigating the murky waters of India&apos;s supplement industry. They witnessed friends buying &quot;genuine&quot; protein powder that barely had any protein. They saw athletes unknowingly consuming fake creatine that delivered zero results. They watched hardworking gym-goers waste their money on counterfeit pre-workouts that were nothing more than caffeine and sugar.
                </p>

                <p>
                  <strong className="text-foreground">Enough was enough.</strong>
                </p>

                <p>
                  In 2020, during the midst of the pandemic when fitness enthusiasts were more vulnerable to online scams than ever, our founders made a decision: if no one else would take responsibility for protecting athletes from fake supplements, they would.
                </p>

                <p>
                  NutraFuel Supplements was established with a single, non-negotiable principle: <strong className="text-foreground">Every single product we sell must be 100% authentic, directly sourced from authorized distributors, and backed by our guarantee.</strong>
                </p>

                <p>
                  What started as a small operation in Siliguri, serving local gyms and athletes, has grown into a trusted name across India and Nepal. But our mission hasn&apos;t changed: we exist to protect the fitness community from counterfeit products and provide the authentic supplements athletes deserve.
                </p>
              </div>

              <div className="mt-12 p-8 rounded-2xl bg-gradient-to-r from-primary/5 via-gold/5 to-primary/5 border border-primary/20">
                <p className="text-lg font-semibold text-foreground mb-2">
                  The Problem We&apos;re Solving
                </p>
                <p className="text-muted-foreground">
                  Studies estimate that up to 30-40% of supplements sold in India are either fake or adulterated. This isn&apos;t just about wasted money – it&apos;s about health risks, destroyed trust, and stolen progress. Athletes work too hard to be cheated by fake products. That&apos;s why NutraFuel exists.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Mission & Vision */}
        <section className="py-20 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="grid md:grid-cols-2 gap-8">
                {/* Mission */}
                <div className="bg-white rounded-2xl p-8 shadow-sm">
                  <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mb-6">
                    <Target className="w-7 h-7 text-primary" />
                  </div>
                  <h2 className="text-2xl font-display font-bold text-foreground mb-4">
                    Our Mission
                  </h2>
                  <p className="text-muted-foreground leading-relaxed mb-6">
                    To eliminate fake supplements from the Indian fitness industry and provide every athlete, bodybuilder, and fitness enthusiast with guaranteed authentic products they can trust.
                  </p>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-success flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-muted-foreground">Source only from authorized distributors</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-success flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-muted-foreground">Verify every product batch for authenticity</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-success flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-muted-foreground">Educate the community about fake supplements</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-success flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-muted-foreground">Protect athletes from counterfeit products</span>
                    </li>
                  </ul>
                </div>

                {/* Vision */}
                <div className="bg-white rounded-2xl p-8 shadow-sm">
                  <div className="w-14 h-14 rounded-full bg-gold/10 flex items-center justify-center mb-6">
                    <TrendingUp className="w-7 h-7 text-gold" />
                  </div>
                  <h2 className="text-2xl font-display font-bold text-foreground mb-4">
                    Our Vision
                  </h2>
                  <p className="text-muted-foreground leading-relaxed mb-6">
                    To become the most trusted sports nutrition brand and retail destination in India, known for our unwavering commitment to authenticity and athlete success.
                  </p>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-muted-foreground">Be the first choice for serious athletes</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-muted-foreground">Set the gold standard for authenticity in India</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-muted-foreground">Expand trusted retail presence nationwide</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-muted-foreground">Create a community of educated supplement users</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Why Choose NutraFuel */}
        <section className="py-20 bg-background">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-3xl sm:text-4xl font-display font-bold text-foreground mb-4">
                  Why Choose NutraFuel?
                </h2>
                <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                  We&apos;re not just another supplement store. We&apos;re your partner in achieving your fitness goals safely and effectively.
                </p>
              </div>

              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {/* Feature 1 */}
                <div className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
                  <div className="w-12 h-12 rounded-full bg-green-500/10 flex items-center justify-center mb-4">
                    <Shield className="w-6 h-6 text-green-600" />
                  </div>
                  <h3 className="text-lg font-bold text-foreground mb-2">
                    100% Authentic
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Every product is sourced directly from authorized distributors. We verify authenticity with batch codes and lab certificates.
                  </p>
                </div>

                {/* Feature 2 */}
                <div className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                    <Award className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="text-lg font-bold text-foreground mb-2">
                    Quality Brands
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    We stock only premium, internationally recognized brands like Optimum Nutrition, MuscleBlaze, Cellucor, and more.
                  </p>
                </div>

                {/* Feature 3 */}
                <div className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
                  <div className="w-12 h-12 rounded-full bg-gold/10 flex items-center justify-center mb-4">
                    <Users className="w-6 h-6 text-gold" />
                  </div>
                  <h3 className="text-lg font-bold text-foreground mb-2">
                    Athlete Trust
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Trusted by competitive athletes, powerlifters, bodybuilders, and fitness professionals across India and Nepal.
                  </p>
                </div>

                {/* Feature 4 */}
                <div className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
                  <div className="w-12 h-12 rounded-full bg-orange-500/10 flex items-center justify-center mb-4">
                    <Heart className="w-6 h-6 text-orange-600" />
                  </div>
                  <h3 className="text-lg font-bold text-foreground mb-2">
                    Expert Support
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Our team of fitness enthusiasts provides personalized supplement recommendations based on your goals.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Brand Values */}
        <section className="py-20 bg-gradient-to-b from-muted/30 to-background">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl sm:text-4xl font-display font-bold text-foreground mb-4">
                  Our Core Values
                </h2>
                <p className="text-lg text-muted-foreground">
                  These principles guide every decision we make
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                {/* Value 1 */}
                <div className="bg-white rounded-xl p-6 border-l-4 border-green-500">
                  <h3 className="text-xl font-bold text-foreground mb-3 flex items-center gap-2">
                    <Shield className="w-5 h-5 text-green-600" />
                    Authenticity
                  </h3>
                  <p className="text-muted-foreground">
                    We never compromise on authenticity. Every product is verified genuine, or it doesn&apos;t make it to our shelves. Your trust is our most valuable asset, and we protect it fiercely.
                  </p>
                </div>

                {/* Value 2 */}
                <div className="bg-white rounded-xl p-6 border-l-4 border-primary">
                  <h3 className="text-xl font-bold text-foreground mb-3 flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-primary" />
                    Performance
                  </h3>
                  <p className="text-muted-foreground">
                    We understand that every rep, every set, every workout matters. We stock only supplements that deliver real results because your progress is our priority.
                  </p>
                </div>

                {/* Value 3 */}
                <div className="bg-white rounded-xl p-6 border-l-4 border-gold">
                  <h3 className="text-xl font-bold text-foreground mb-3 flex items-center gap-2">
                    <Users className="w-5 h-5 text-gold" />
                    Community
                  </h3>
                  <p className="text-muted-foreground">
                    We&apos;re more than a store – we&apos;re part of the fitness community. We support athletes, sponsor events, and contribute to making fitness accessible and safe for everyone.
                  </p>
                </div>

                {/* Value 4 */}
                <div className="bg-white rounded-xl p-6 border-l-4 border-orange-500">
                  <h3 className="text-xl font-bold text-foreground mb-3 flex items-center gap-2">
                    <Award className="w-5 h-5 text-orange-600" />
                    Trust
                  </h3>
                  <p className="text-muted-foreground">
                    Trust is earned, not given. We earn yours every day through transparency, consistent quality, and standing behind every product we sell with our authenticity guarantee.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Closing Statement & CTA */}
        <section className="py-20 bg-gradient-to-r from-primary via-primary to-primary/90 text-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl sm:text-4xl font-display font-bold mb-6">
                Your Fitness Journey Deserves Authentic Fuel
              </h2>
              <p className="text-lg text-white/90 leading-relaxed mb-8 max-w-3xl mx-auto">
                Whether you&apos;re a competitive athlete, a dedicated bodybuilder, or just starting your fitness journey, you deserve supplements you can trust. At NutraFuel, authenticity isn&apos;t a marketing claim – it&apos;s our founding principle.
              </p>
              <p className="text-xl font-semibold text-white mb-10">
                Train hard. Fuel right. Trust NutraFuel.
              </p>

              <div className="flex flex-wrap gap-4 justify-center">
                <Link 
                  to="/products" 
                  className="inline-flex items-center gap-2 px-8 py-4 bg-white text-primary font-bold rounded-lg hover:bg-white/90 transition-colors shadow-lg"
                >
                  Shop Authentic Products
                </Link>
                <Link 
                  to="/stores" 
                  className="inline-flex items-center gap-2 px-8 py-4 bg-transparent border-2 border-white text-white font-bold rounded-lg hover:bg-white/10 transition-colors"
                >
                  Visit Our Stores
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-12 bg-muted/30 border-t border-border">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
                <div>
                  <div className="text-4xl font-bold text-primary mb-2">100%</div>
                  <div className="text-sm text-muted-foreground">Authentic Products</div>
                </div>
                <div>
                  <div className="text-4xl font-bold text-primary mb-2">50+</div>
                  <div className="text-sm text-muted-foreground">Partner Athletes</div>
                </div>
                <div>
                  <div className="text-4xl font-bold text-primary mb-2">10K+</div>
                  <div className="text-sm text-muted-foreground">Happy Customers</div>
                </div>
                <div>
                  <div className="text-4xl font-bold text-primary mb-2">2</div>
                  <div className="text-sm text-muted-foreground">Store Locations</div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </Layout>
    </>
  );
};

export default AboutPage;
